import type { MarginTerm } from "@yutai/shared"
import { useMemo, useState } from "react"
import styles from "./MarginInventoryDiffPanel.module.css"
import { MAX_DIFF_BACK, buildTermDiff, buildTweets, formatDelta } from "./inventoryDiff.js"
import { MARGIN_TERM_LABELS } from "./marginInventoryCsv.js"
import type { ThisMonthInventoryRow } from "./thisMonthInventory.js"

const BACK_OPTIONS = Array.from({ length: MAX_DIFF_BACK + 1 }, (_, n) => n)

function formatShortDate(date: string | null): string {
  if (!date) return "—"
  const [, m, d] = date.split("-")
  return `${Number(m)}/${Number(d)}`
}

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    // 非HTTPSなどで Clipboard API が使えない場合のフォールバック
    const ta = document.createElement("textarea")
    ta.value = text
    ta.style.position = "fixed"
    ta.style.opacity = "0"
    document.body.appendChild(ta)
    ta.select()
    const ok = document.execCommand("copy")
    document.body.removeChild(ta)
    return ok
  }
}

function CopyIcon({ done }: { done: boolean }) {
  return done ? (
    <svg
      viewBox="0 0 24 24"
      width="20"
      height="20"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      aria-hidden="true"
    >
      <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ) : (
    <svg
      viewBox="0 0 24 24"
      width="20"
      height="20"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <rect x="9" y="9" width="11" height="11" rx="2" />
      <path d="M5 15V6a2 2 0 0 1 2-2h9" strokeLinecap="round" />
    </svg>
  )
}

function TweetBox({ text }: { text: string }) {
  const [copied, setCopied] = useState(false)
  async function handleCopy() {
    if (await copyText(text)) {
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    }
  }
  return (
    <div className={styles.tweet}>
      <pre className={styles.tweetText}>{text}</pre>
      <div className={styles.tweetFoot}>
        <span className={styles.count}>{Array.from(text).length}/140字</span>
        <button
          type="button"
          className={styles.copyButton}
          onClick={handleCopy}
          aria-label="クリップボードにコピー"
          title="クリップボードにコピー"
        >
          <CopyIcon done={copied} />
        </button>
      </div>
    </div>
  )
}

interface Props {
  rows: ThisMonthInventoryRow[]
  month: number
}

// 直近アップロードと前回の差分（リロードしても /latest の prevQuantity から再計算されて残る）と、
// その差分から作るX投稿文。
export function MarginInventoryDiffPanel({ rows, month }: Props) {
  const [back, setBack] = useState(0)
  const diffs = useMemo(
    () =>
      (["unlimited", "14d"] as const)
        .map((term) => buildTermDiff(rows, term, back))
        .filter((d): d is NonNullable<typeof d> => d !== null),
    [rows, back]
  )
  const [selected, setSelected] = useState<MarginTerm | null>(null)
  const [showTweet, setShowTweet] = useState(false)

  if (diffs.length === 0 && back === 0) return null
  if (diffs.length === 0) {
    return (
      <section className={styles.panel}>
        <h2 className={styles.title}>前回アップロードからの変化</h2>
        <p className={styles.notice}>これ以上前のアップロードはありません</p>
        <button type="button" className={styles.tab} onClick={() => setBack(0)}>
          直近に戻る
        </button>
      </section>
    )
  }
  const diff = diffs.find((d) => d.term === selected) ?? diffs[0]
  const tweets = showTweet ? buildTweets(diff, month) : []

  return (
    <section className={styles.panel}>
      <h2 className={styles.title}>前回アップロードからの変化</h2>

      {diffs.length > 1 && (
        <div className={styles.tabs} role="tablist">
          {diffs.map((d) => (
            <button
              key={d.term}
              type="button"
              role="tab"
              aria-selected={d.term === diff.term}
              className={d.term === diff.term ? styles.tabActive : styles.tab}
              onClick={() => setSelected(d.term)}
            >
              {MARGIN_TERM_LABELS[d.term]}
            </button>
          ))}
        </div>
      )}

      <div className={styles.tabs} role="tablist" aria-label="遡る回数">
        {BACK_OPTIONS.map((n) => (
          <button
            key={n === 0 ? "latest" : `back-${n}`}
            type="button"
            role="tab"
            aria-selected={n === back}
            className={n === back ? styles.tabActive : styles.tab}
            onClick={() => setBack(n)}
          >
            {n === 0 ? "直近" : `${n}回前`}
          </button>
        ))}
      </div>

      <p className={styles.meta}>
        {MARGIN_TERM_LABELS[diff.term]} {formatShortDate(diff.prevSnapshotDate)} →{" "}
        {formatShortDate(diff.snapshotDate)}
        {diff.prevSnapshotDate &&
          ` ・ 変化 ${diff.changed.length}銘柄 / 変化なし ${diff.unchangedCount}銘柄`}
      </p>

      {!diff.prevSnapshotDate ? (
        <p className={styles.notice}>比較できる前回のアップロードがありません</p>
      ) : diff.changed.length === 0 ? (
        <p className={styles.notice}>前回から変化はありません</p>
      ) : (
        <ul className={styles.list}>
          {diff.changed.map((i) => (
            <li key={i.code} className={styles.item}>
              <span className={styles.name}>
                {i.name}
                <span className={styles.code}>{i.code}</span>
              </span>
              <span className={styles.qty}>
                {i.prevQuantity === null ? "—" : i.prevQuantity.toLocaleString("ja-JP")} →{" "}
                {i.quantity === null ? "—" : i.quantity.toLocaleString("ja-JP")}
              </span>
              <span className={i.delta > 0 ? styles.up : styles.down}>{formatDelta(i)}</span>
            </li>
          ))}
        </ul>
      )}

      {diff.prevSnapshotDate && (
        <button
          type="button"
          className={styles.xButton}
          onClick={() => setShowTweet((v) => !v)}
          aria-expanded={showTweet}
        >
          <span aria-hidden="true">𝕏</span> {showTweet ? "投稿文を閉じる" : "投稿文を作る"}
        </button>
      )}
      {tweets.map((t) => (
        <TweetBox key={t} text={t} />
      ))}
    </section>
  )
}
